// ─────────────────────────────────────────────────────────────────────────────
// Web Push feature detection + the pure decisions behind the notification UI.
//
// Platform notes:
//   • Desktop (Chrome/Edge/Firefox/Safari 16+) and Android: push works in a tab.
//   • iOS/iPadOS: push works ONLY when the app is opened from the Home Screen
//     (standalone web app, iOS 16.4+). In a Safari tab PushManager is missing, so we
//     show the "Add to Home Screen" steps instead of a button that can do nothing.
//   • Browsers inside social apps (Instagram, Facebook, TikTok…) support neither.
// ─────────────────────────────────────────────────────────────────────────────

import {
  NotificationDialogDecision,
  NotificationsSectionStatus,
  RestTimerNotificationHint,
  UserRole,
} from "../../enums";
import { INotificationPromptHistory } from "./notificationPromptStorageUtility";

/** Days the dialog stays away from a device after it appeared there, or after "Не сега". */
export const NOTIFICATION_PROMPT_RETRY_DAYS = 30;

/** After this many "Не сега" on a device, the dialog stops appearing there. */
export const NOTIFICATION_PROMPT_MAX_DISMISSALS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

// Markers that social apps add to the user agent of their built-in browser.
const IN_APP_BROWSER_PATTERN =
  /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|musical_ly|BytedanceWebview|TikTok|Snapchat|LinkedInApp|Twitter| Line\//;

/**
 * Converts a base64url-encoded VAPID public key into the Uint8Array that
 * PushManager.subscribe expects as its applicationServerKey.
 */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/** True when the browser exposes the Service Worker + Push + Notification APIs. */
export const isPushSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator &&
  typeof window !== "undefined" &&
  "PushManager" in window &&
  "Notification" in window;

/** The browser's notification permission, or "default" where the API does not exist. */
export const getNotificationPermission = (): NotificationPermission =>
  typeof Notification !== "undefined" ? Notification.permission : "default";

/** True when the app runs as an installed / Home Screen web app. */
export const isStandalone = (): boolean => {
  if (typeof window === "undefined") return false;

  const displayModeStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;

  // iOS Safari exposes navigator.standalone for Home Screen web apps.
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;

  return displayModeStandalone || iosStandalone;
};

/** True on any iPhone/iPad (including iPadOS, which reports itself as "Macintosh"). */
export const isIos = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";

  const isIPhoneIpad = /iPad|iPhone|iPod/.test(ua);
  const isIpadOs =
    ua.includes("Macintosh") &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;

  return isIPhoneIpad || isIpadOs;
};

/** True on an iPhone/iPad browsing in a tab (not opened from the Home Screen). */
export const isIosSafariTab = (): boolean => isIos() && !isStandalone();

/** True inside the built-in browser of a social app, where push is unavailable. */
export const isInAppBrowser = (
  userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : ""
): boolean => IN_APP_BROWSER_PATTERN.test(userAgent);

export interface IReservationNotificationDialogParams {
  flagEnabled: boolean;
  isConfirmedReservation: boolean;
  supported: boolean;
  permission: NotificationPermission;
  iosSafariTab: boolean;
  inAppBrowser: boolean;
  history: INotificationPromptHistory;
  /** When the dialog last appeared on this device, for any audience, or null. */
  lastShownAt: string | null;
  now: Date;
}

interface INotificationDialogRules {
  supported: boolean;
  permission: NotificationPermission;
  iosSafariTab: boolean;
  history: INotificationPromptHistory;
  /** When the dialog last appeared on this device, for any audience, or null. */
  lastShownAt: string | null;
  now: Date;
  /** After this many "Не сега" the dialog stops appearing; null means no limit. */
  maxDismissals: number | null;
}

/** A time in the future (the clock was wrong when it was saved) does not hold the dialog back. */
const isWithinRetryWindow = (at: string | null, now: Date): boolean => {
  if (!at) return false;
  const elapsedMs = now.getTime() - Date.parse(at);
  return elapsedMs >= 0 && elapsedMs < NOTIFICATION_PROMPT_RETRY_DAYS * DAY_MS;
};

/** The device and "Не сега" rules shared by the client and the staff dialog. */
const decideByDeviceAndHistory = ({
  supported,
  permission,
  iosSafariTab,
  history,
  lastShownAt,
  now,
  maxDismissals,
}: INotificationDialogRules): NotificationDialogDecision => {
  if (maxDismissals !== null && history.dismissCount >= maxDismissals) {
    return NotificationDialogDecision.None;
  }

  // Seen on this device within the last 30 days, whatever the answer was.
  if (isWithinRetryWindow(lastShownAt, now) || isWithinRetryWindow(history.lastDismissedAt, now)) {
    return NotificationDialogDecision.None;
  }

  // On an iOS Safari tab the push API is missing; installing to the Home Screen is the
  // only way to notifications there, so this branch comes before the support check.
  if (iosSafariTab) {
    return NotificationDialogDecision.IosInvite;
  }

  if (!supported || permission !== "default") {
    // Unsupported, or the user already allowed / blocked notifications.
    return NotificationDialogDecision.None;
  }

  return NotificationDialogDecision.Invite;
};

/** Decides whether the reservation-success screen invites the user to turn on notifications. */
export const getReservationNotificationDialogDecision = ({
  flagEnabled,
  isConfirmedReservation,
  inAppBrowser,
  ...rules
}: IReservationNotificationDialogParams): NotificationDialogDecision => {
  if (!flagEnabled || !isConfirmedReservation || inAppBrowser) {
    return NotificationDialogDecision.None;
  }

  return decideByDeviceAndHistory({ ...rules, maxDismissals: NOTIFICATION_PROMPT_MAX_DISMISSALS });
};

/** Every role except clients (employees, the studio owner and GlobalAdmin). */
export const isStaffRole = (role: UserRole | undefined): boolean =>
  role !== undefined && role !== UserRole.User;

export interface IStaffNotificationDialogParams {
  flagEnabled: boolean;
  isStaff: boolean;
  supported: boolean;
  permission: NotificationPermission;
  iosSafariTab: boolean;
  inAppBrowser: boolean;
  history: INotificationPromptHistory;
  /** When the dialog last appeared on this device, for any audience, or null. */
  lastShownAt: string | null;
  now: Date;
}

/**
 * Decides whether staff are invited to turn on notifications when they open the platform.
 * Unlike clients there is no limit: they are asked again every 30 days until they allow.
 */
export const getStaffNotificationDialogDecision = ({
  flagEnabled,
  isStaff,
  inAppBrowser,
  ...rules
}: IStaffNotificationDialogParams): NotificationDialogDecision => {
  if (!flagEnabled || !isStaff || inAppBrowser) {
    return NotificationDialogDecision.None;
  }

  return decideByDeviceAndHistory({ ...rules, maxDismissals: null });
};

export interface INotificationsSectionStatusParams {
  supported: boolean;
  permission: NotificationPermission;
  iosSafariTab: boolean;
  inAppBrowser: boolean;
  hasSubscription: boolean;
}

/** State of the "Известия" tab in "Моят профил" for the current device. */
export const getNotificationsSectionStatus = ({
  supported,
  permission,
  iosSafariTab,
  inAppBrowser,
  hasSubscription,
}: INotificationsSectionStatusParams): NotificationsSectionStatus => {
  if (inAppBrowser) return NotificationsSectionStatus.Unsupported;
  if (iosSafariTab) return NotificationsSectionStatus.IosInstall;
  if (!supported) return NotificationsSectionStatus.Unsupported;
  if (permission === "denied") return NotificationsSectionStatus.Blocked;
  if (permission === "granted" && hasSubscription) return NotificationsSectionStatus.Enabled;
  return NotificationsSectionStatus.Disabled;
};

export interface IRestTimerNotificationHintParams {
  flagEnabled: boolean;
  /** This device's notifications state (the same one the "Известия" tab shows). */
  status: NotificationsSectionStatus;
  /** "×" history of this line, kept apart from the dialog's. */
  history: INotificationPromptHistory;
  now: Date;
}

/**
 * The line under the workout rest timer. Without notifications on this device the timer cannot
 * ring while the phone is locked, so it offers to turn them on, or on an iPhone Safari tab shows
 * how to add Planny to the Home Screen. "×" hides it under the client dialog's "Не сега" rules.
 */
export const getRestTimerNotificationHint = ({
  flagEnabled,
  status,
  history,
  now,
}: IRestTimerNotificationHintParams): RestTimerNotificationHint | null => {
  if (!flagEnabled) return null;

  if (
    history.dismissCount >= NOTIFICATION_PROMPT_MAX_DISMISSALS ||
    isWithinRetryWindow(history.lastDismissedAt, now)
  ) {
    return null;
  }

  if (status === NotificationsSectionStatus.Disabled) return RestTimerNotificationHint.Enable;
  if (status === NotificationsSectionStatus.IosInstall) return RestTimerNotificationHint.IosInstall;
  return null;
};

export interface INotificationsTabParams {
  role: UserRole | undefined;
  appNotificationsEnabled: boolean;
}

/**
 * The "Известия" tab in "Моят профил". Staff always see it (the "new reservations" email
 * does not need app notifications); clients only have device notifications there, so they
 * see it only while the enable-app-notifications flag is on.
 */
export const canSeeNotificationsTab = ({
  role,
  appNotificationsEnabled,
}: INotificationsTabParams): boolean => {
  if (role === undefined) return false;
  return isStaffRole(role) || appNotificationsEnabled;
};

const SERVICE_WORKER_PATH = "/service-worker.js";

/**
 * The service worker confirms each notification back to the API ("delivered", "opened"),
 * but it cannot read the app's env, so the API address travels in its script URL.
 */
export const getServiceWorkerUrl = (apiBaseUrl: string): string =>
  `${SERVICE_WORKER_PATH}?api=${encodeURIComponent(apiBaseUrl)}`;

/**
 * True when this browser runs an older worker (plain /service-worker.js, or another API
 * address) that should be replaced by expectedUrl. False when there is no worker at all:
 * someone who never turned notifications on must not get one installed.
 */
export const shouldReRegisterServiceWorker = (
  activeScriptUrl: string | null | undefined,
  expectedUrl: string
): boolean => {
  if (!activeScriptUrl) return false;
  try {
    const active = new URL(activeScriptUrl);
    const expected = new URL(expectedUrl, active.origin);
    return active.pathname + active.search !== expected.pathname + expected.search;
  } catch {
    return false;
  }
};
